import {
  createGenericView as engineCreateGenericView,
  createView as engineCreateView,
} from '@sail/engine';
import {usesCssSerializer} from './css';

export {
  assignConditionalTokens,
  assignTokens,
  useTokenValue,
  createViewConfig,
  media,
} from '@sail/engine';

export {
  darkMode,
  darkModeComponentOverrides,
  darkModeWebContentOverrides,
  dashboardAppTokenOverrides,
  darkModeTokenOverrides,
  darkModeWebContent,
  themeOverrides,
} from './themes';

/**
 * Typescript [Generics](https://www.typescriptlang.org/docs/handbook/2/generics.html) and [Generic Constraints](https://www.typescriptlang.org/docs/handbook/2/generics.html#generic-constraints) can be used to create [views](/apis/createview) that support and provide type checking against a variety of types.
 *
 * @see https://sail.stripe.me/apis/creategenericview/
 */
export const createGenericView = usesCssSerializer(engineCreateGenericView);
/**
 * Sail's entry point for creating components. Views extend React components to make them more configurable, styleable, and easily composable by default.
 *
 * @see https://sail.stripe.me/apis/createview/
 */
export const createView = usesCssSerializer(engineCreateView);

export * from './view';

export * from './components/Accordion';
export * from './components/AccordionItem';
export * from './components/Action';

export type {AddressDisplayUnits} from './components/AddressDisplay';
export {AddressDisplay} from './components/AddressDisplay';
export {AddressField, AddressFieldConfig} from './components/address-field';
export type {Key} from '@react-types/shared';

export type {
  AddressFieldCountryCode,
  AddressValue,
  AddressValueAdapter,
  AddressAutocompleteItem,
  SubregionOverride,
} from './components/address-field';
export type {
  AddressFieldSchema,
  AddressField as AddressFieldType,
} from './components/addressData/schemaTypes';
export {addressFieldLabels} from './components/addressData/addressFieldLabels';
export type {FieldLabelOverrides} from './components/addressData/addressFieldLabels';
export {useAddressData} from './components/addressData/useAddressData';
export type {
  AddressData,
  AddressCountryCode,
} from './components/addressData/useAddressData';
export {Avatar, AvatarConfig} from './components/avatar';
export type {AvatarProps} from './components/avatar';

export * from './components/Backdrop';
export * from './components/Badge';
export * from './components/Banner';
export * from './components/BasicTable';
export * from './components/Breadcrumbs';
export * from './components/Button';
export * from './components/ButtonGroup';
export * from './components/Callout';
export * from './components/Checkbox';
export * from './components/Chip';
export {
  Coachmark as DO_NOT_USE_Coachmark,
  CoachmarkConfig,
} from './components/Coachmark';
export type {CoachmarkProps} from './components/Coachmark';
export * from './components/code-puncher';
export * from './components/CollapsibleContainer';
export {
  CompositeFieldConfig,
  CompositeField,
} from './components/CompositeField';
export type {CompositeFieldProps} from './components/CompositeField';
export * from './components/Counter';
export * from './components/CurrencyField';
export * from './components/DateField';
export * from './components/date-picker';
export type {DialogProps} from './components/dialog/DialogProps';
export {Dialog} from './components/dialog/Dialog';
export {DialogConfig} from './components/dialog/DialogConfig';
export type {DialogContentProps} from './components/dialog/DialogContent';
export {
  DialogContentConfig,
  DialogContent,
} from './components/dialog/DialogContent';
export type {DialogFooterProps} from './components/dialog/DialogFooter';
export {
  DialogFooterConfig,
  DialogFooter,
} from './components/dialog/DialogFooter';
export type {DialogHeaderProps} from './components/dialog/DialogHeader';
export {
  DialogHeaderConfig,
  DialogHeader,
} from './components/dialog/DialogHeader';
export {
  DialogCloseButtonConfig,
  DialogCloseButton,
} from './components/dialog/DialogCloseButton';
export type {DialogContextType} from './components/dialog/useDialogContext';
export {
  DialogContext,
  useDialogContext,
} from './components/dialog/useDialogContext';
export * from './components/Divider';
export * from './components/DocumentTitle';
export {
  CreationDrawer,
  CreationDrawerConfig,
  CreationDrawerFooter,
  CreationDrawerFooterConfig,
  CreationDrawerHeader,
  CreationDrawerHeaderConfig,
  Drawer,
  InlineDrawer,
  InlineDrawerConfig,
  InlineDrawerContext,
  InlineDrawerProvider,
  useDrawerClose,
} from './components/drawer';
export {DrawerConfig} from './components/drawer/DrawerConfig';
export type {DrawerProps} from './components/drawer/DrawerProps';
export type {
  CreationDrawerFooterProps,
  CreationDrawerHeaderProps,
  CreationDrawerProps,
  InlineDrawerProps,
} from './components/drawer';
export * from './components/EmptyPage';
export * from './components/EmptyState';
export * from './components/file-uploader';
export * from './components/form';
export {
  FormField,
  FormFieldConfig,
  useFormFieldProps,
  FormFieldMetadataContext,
} from './components/FormField';
export * from './components/FormFieldGroup';
export type {FormFieldProps} from './components/FormField';
export {HeadingConfig, Heading} from './components/Heading';
export {
  useMediaAnyCoarsePointer,
  useMediaSmallScreenAndTouch,
  useIsMobileAndTouchCheck,
} from './components/hooks/useMatchMedia/useClientMatchMedia';
export * from './components/hooks/useCollapsible';
export * from './components/hooks/useCompositeErrors';
export * from './components/hooks/useMutationObserver';
export * from './components/hooks/usePress';
export * from './components/hooks/useResizeObserver';
export * from './components/Icon';
export * from './components/LazyIcons';
export * from './components/Input';
export * from './components/InlineFeedback';
export {
  LAYERS_CONTAINER_DEFAULT_Z,
  NAVIGATE_EVENT,
  Unstable_ActionHrefAdapterContext,
  Unstable_ActionNavigateContext,
  type NavigateHandler,
  type Href,
  type ActionOverrideProps,
} from '@sail/global-state';
export * from './components/layers/FocusScope';
export {
  Layer,
  LayerConfig,
  RootLayer,
  RootLayerConfig,
  useCurrentLayer,
  useRootLayer,
} from './components/layers/Layer';
export {useInteractOutside} from './components/layers/vendor/useInteractOutside';
export * from './components/Label';
export * from './components/Link';
export {ListBox, ListBoxConfig, ListBoxContext} from './components/ListBox';
export * from './components/ListBoxGroup';
export * from './components/ListBoxItem';
export * from './components/Menu';
export * from './components/MenuConfig';
export {
  NativeSelect,
  NativeSelectConfig,
  Select,
  SelectConfig,
} from './components/Select';
export type {NativeSelectProps} from './components/Select';
export {NativeTextArea, TextArea, TextAreaConfig} from './components/TextArea';
export type {NativeTextAreaProps} from './components/TextArea';
export {
  NativeTextField,
  NativeTextFieldConfig,
  TextField,
  TextFieldConfig,
} from './components/TextField';
export type {NativeTextFieldProps} from './components/TextField';
export * from './components/Page';
export * from './components/PageHeader';
export {
  PageModule,
  PageModuleConfig,
  PageModuleContext,
} from './components/PageModule';
export * from './components/PageSection';
export * from './components/percent-field';
export {ListItemConfig, ListItem, ListConfig, List} from './components/List';
export type {ListItemProps, ListProps} from './components/List';
export {
  TabsConfig,
  TabConfig,
  TabListConfig,
  TabPanelConfig,
  TabList,
  TabPanelsConfig,
  Tabs,
  Tab,
  TabPanel,
  TabPanels,
  StyledTabConfig,
} from './components/Tabs';

export {default as useFormattedPhoneNumber} from './hooks/useFormattedPhoneNumber/useFormattedPhoneNumber';
export * from './components/PhoneNumberField';
export * from './components/PhoneNumberFieldConfig';

export * from './components/ProgressIndicator';
export * from './components/PropertyList';
export * from './components/popover';
export * from './components/Radio';
export * from './components/SearchField';
export * from './components/SearchFieldConfig';
export * from './components/SelectField';
export * from './components/SelectFieldConfig';
export * from './components/Signpost';
export * from './components/Skeleton';
export * from './components/Spinner';
export * from './components/StarterGuide';
export * from './components/StarterGuideBanner';
export * from './components/StarterGuidePopover';
export * from './components/SSRProvider';
export * from './components/StripeLogo';
export * from './components/Switch';
export * from './components/table';
export * from './components/Tag';
export * from './components/TaskList';
export * from './components/Timeline';
export * from './components/toast';
export * from './components/Toggle';
export * from './components/Tooltip';

export {
  calculateIframePosition,
  safeAreaExists,
  getSafeAreaDimensions,
} from './iframe-overrides/calculateIframePosition';
export type {
  IUILayerRepositionValues,
  AttachmentPoints,
  PlacementInput,
  SafeAreaDimensions,
} from './iframe-overrides/calculateIframePosition';
export {IframeScrollbarStyleProvider} from './iframe-overrides/IframeScrollbarStyleProvider';
export {
  FocusContainmentProvider,
  FocusContainmentContext,
  useFocusContainment,
} from './iframe-overrides/FocusContainmentProvider';
export {useIsWithinListBoxContext} from './iframe-overrides/utils';
export type {RequestIframe} from './iframe-overrides/types';
export type {MobileDisplay} from './utilStyles/sheet';
export * from './hooks/useInteractAnywhere';

export {css, external} from './css';
export * from '@sail/tokens/deprecated';
// FIXME: Fix hsluv-ts conflicts before exposing: go/pr/846843
// export {UNSTABLE_generateColorStep} from './colors/UNSTABLE_generateColorStep';
export * from './providers/UiProvider';
// These exports are used by the `SailCustomisableAddressField` connect component
export {default as CONNECT_ONLY_addressSchemasJSON} from './components/addressData/addressSchemas.json';
export {default as CONNECT_ONLY_defaultCountryLabels} from './components/addressData/countryLabels.json';

export type {Font} from './css';
export type {CssProperties} from './css';

export {FauxTreeSelectAllItemConfig as DO_NOT_USE_FauxTreeSelectAllItemConfig} from './components/FauxTreeSelectAllItem';
export {FauxTreeGroup as DO_NOT_USE_FauxTreeGroup} from './components/FauxTreeGroup';
export {FauxTreeItem as DO_NOT_USE_FauxTreeItem} from './components/FauxTreeItem';
export {DO_NOT_USE_FauxTree} from './components/FauxTree';

export {useRpcMatchMedia as DO_NOT_USE_useRpcMatchMedia} from './components/hooks/useMatchMedia/useRpcMatchMedia';
export {uiRpc} from './lib/ui-rpc/rpc';
